import React, { useState, useCallback, useMemo, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  AddOnV3,
  ListingAddOnLimitType,
  ListingAddOnUnavailableReason,
} from '@kouto/types';
import { currencyFormat } from 'utils';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import { useCalculateAdvertisedTierPrice } from 'utils/calculateAdvertisedTierPrice';
import type { ITax } from 'types/experience.types';
import IconPlus from 'assets/icon-plus';
import IconMinus from 'assets/icon-minus';
import AddOnDetailsModal from './AddOnDetailsModal';

interface AddOnCardProps {
  addOn: AddOnV3;
  gridArea: string;
  selectedPriceTiersCount: number;
  currentAmount: number;
  unavailableReason?: ListingAddOnUnavailableReason;
  onAmountChange: (amount: number) => void;
}

const getPictureUrl = (picture: AddOnV3['picture']) => {
  if (!picture) {
    return '';
  }

  return (
    picture.uri['360w'] ||
    picture.uri['720w'] ||
    picture.uri['1080w'] ||
    picture.uri.original
  );
};

const AddOnCard = ({
  addOn,
  gridArea,
  selectedPriceTiersCount,
  currentAmount,
  unavailableReason,
  onAmountChange,
}: AddOnCardProps) => {
  const [detailsModalOpened, setDetailsModalOpened] = useState(false);
  const unavailable = Boolean(unavailableReason);
  const currency = useBrandCurrency();
  const { t } = useTranslation();
  let unavailableLabel: string | undefined;
  if (unavailableReason === ListingAddOnUnavailableReason.BOOKING_CUTOFF) {
    unavailableLabel = t('salesClosed');
  } else if (unavailableReason) {
    unavailableLabel = t('soldOut');
  }
  const calculateAdvertisedTierPrice = useCalculateAdvertisedTierPrice();
  const priceLabel = useMemo(
    () =>
      addOn.price
        ? currencyFormat(currency)(
            calculateAdvertisedTierPrice(
              addOn.price,
              addOn.taxes as unknown as ITax[],
            ),
          )
        : t('complimentary'),
    [currency, addOn.price, addOn.taxes, calculateAdvertisedTierPrice, t],
  );

  const amountLimit = useMemo(() => {
    if (!addOn.limit) {
      return Infinity;
    }
    if (
      addOn.limitType === ListingAddOnLimitType.PER_BOOKING ||
      addOn.limitType === ListingAddOnLimitType.PER_SESSION
    ) {
      return addOn.limit;
    }
    return addOn.limit * selectedPriceTiersCount;
  }, [addOn.limit, addOn.limitType, selectedPriceTiersCount]);

  const openDetailsModal = useCallback(() => {
    setDetailsModalOpened(true);
  }, []);

  const closeDetailsModal = useCallback(() => {
    setDetailsModalOpened(false);
  }, []);

  const onAdd = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onAmountChange(Math.min(amountLimit || Infinity, currentAmount + 1));
    },
    [currentAmount, amountLimit, onAmountChange],
  );

  const onRemove = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onAmountChange(Math.max(0, currentAmount - 1));
    },
    [currentAmount, onAmountChange],
  );

  return (
    <>
      {detailsModalOpened && (
        <AddOnDetailsModal addOn={addOn} onClose={closeDetailsModal} />
      )}
      <Wrapper
        gridArea={gridArea}
        unavailable={unavailable}
        className="add-on-card"
        onClick={unavailable ? undefined : openDetailsModal}
        aria-disabled={unavailable}
        aria-haspopup={unavailable ? undefined : 'dialog'}
        aria-controls={
          unavailable ? undefined : t('addOnModal', { addOn: addOn.label })
        }
        aria-label={
          unavailableLabel
            ? `${addOn.label}: ${unavailableLabel}`
            : t('moreInformationAboutAddOn', { addOn: addOn.label })
        }
      >
        <Details className="add-on-card-details">
          <Title className="add-on-card-title">{addOn.label}</Title>
          <Price className="add-on-card-price">{priceLabel}</Price>
          {addOn.description && (
            <Description className="add-on-card-description">
              {addOn.description}
            </Description>
          )}
        </Details>
        {addOn.picture && (
          <Image
            className="add-on-card-image"
            alt={t('imageOf', { name: addOn.label })}
            title={t('imageOf', { name: addOn.label })}
            src={getPictureUrl(addOn.picture)}
          />
        )}
        <Actions>
          {unavailableLabel ? (
            <UnavailableLabel className="add-on-card-unavailable-label">
              {unavailableLabel}
            </UnavailableLabel>
          ) : (
            <ButtonsWrapper className="add-on-card-buttons-container">
              <button
                data-testid={`${addOn.label}_addon_minus`}
                type="button"
                className="add-on-card-button-minus"
                aria-label={t('decreaseAddOnAmount', { addOn: addOn.label })}
                disabled={unavailable || currentAmount <= 0}
                onClick={onRemove}
              >
                <IconMinus color="var(--way-palette-black-80)" />
              </button>
              <div className="add-on-card-amount">
                <span>{unavailable ? 0 : currentAmount}</span>
              </div>
              <button
                data-testid={`${addOn.label}_addon_plus`}
                type="button"
                className="add-on-card-button-plus"
                aria-label={t('increaseAddOnAmount', { addOn: addOn.label })}
                disabled={unavailable || currentAmount >= amountLimit}
                onClick={onAdd}
              >
                <IconPlus color="var(--way-palette-black-80)" />
              </button>
            </ButtonsWrapper>
          )}
        </Actions>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div<{ gridArea: string; unavailable: boolean }>`
  position: relative;
  min-height: 126px;
  border: solid 0.5px var(--way-colors-borderColor);
  transition-duration: 0.2s;
  cursor: ${({ unavailable }) => (unavailable ? 'not-allowed' : 'pointer')};

  &:hover {
    border: solid 0.5px var(--way-colors-borderHoverColor);
  }
`;

const Details = styled.div`
  width: calc(100% - 124px);
  padding: 16px;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  overflow: hidden;
  white-space: nowrap;
  letter-spacing: 0.016px;
  text-overflow: ellipsis;
  margin: 0px;
  color: var(--way-colors-primaryTextColor);
`;

const Price = styled.span`
  margin: 0px;
  height: 17px;
  color: var(--way-colors-primaryTextColor);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.014px;
`;

/* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix */
const Description = styled.p`
  margin: 0px;
  margin-top: 12px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.014px;
  color: var(--way-colors-contrastColorShades-60);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;
/* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */

const Image = styled.img<{ src: string }>`
  width: 124px;
  height: 100%;
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 10;
  object-fit: cover;
  background: linear-gradient(
      180deg,
      var(--way-palette-black-50),
      var(--way-palette-black-20) 100%
    ),
    url('${({ src }) => src}') lightgray 50% / cover no-repeat;
`;

const Actions = styled.div`
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 20;
`;

const UnavailableLabel = styled.span`
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0px 12px;
  white-space: nowrap;
  color: var(--way-colors-errorColor);
  /* Frosted base keeps the tint legible when the badge sits over a photo. */
  background: var(--way-palette-white-80);
  background-image: linear-gradient(
    var(--way-palette-red-10),
    var(--way-palette-red-10)
  );
  backdrop-filter: blur(8px);
  box-shadow: var(--way-design-boxShadow-s);
  border-radius: var(--way-design-borderRadiusDefault);
  font-size: 12px;
  font-weight: 600;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  width: 84px;
  height: 28px;
  background: var(--way-palette-white-80);
  backdrop-filter: blur(8px);
  box-shadow: var(--way-design-boxShadow-s);

  & > * {
    width: 28px;
    height: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  & button {
    cursor: pointer;
    border: none;
    background: none;
    padding: 0px;
    border-radius: var(--way-design-borderRadiusDefault);
    transition: all 0.2s ease;

    & path {
      stroke: transparent;
      fill: var(--way-palette-black-70);
    }

    &:hover {
      background: var(--way-palette-black-5);
    }
    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
    &:disabled:hover {
      background: none;
    }
  }

  & span {
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    color: var(--way-palette-black-80);
  }
`;

export default AddOnCard;
